import { computed, ref, type ComputedRef, type Ref } from "vue"
import type { MediaUpdateInput } from "../../graphql/types"
import {
  computePerItemArray,
  type ArrayField,
  type ArrayMode,
  type BatchEditItem,
  type EntityRef,
  type ScalarField
} from "./batchEditItem"
import { seriesFilename } from "./batchFilename"

export type WatermarkMode = "noop" | "on" | "off"

export interface FailedItem {
  uuid: string
  filename: string
  error: Error
}

export interface BatchResult {
  succeeded: string[]
  failed: FailedItem[]
}

export interface ArrayPatch {
  mode: ArrayMode
  values: EntityRef[]
}

export interface BatchEditSessionOptions {
  items: Ref<BatchEditItem[]>
  save: (id: string, update: MediaUpdateInput) => Promise<void>
}

export interface BatchEditSession {
  items: Ref<BatchEditItem[]>
  removeItem: (uuid: string) => void

  scalarPatch: Ref<Partial<Record<ScalarField, string>>>
  watermarkMode: Ref<WatermarkMode>
  arrayPatches: Ref<Partial<Record<ArrayField, ArrayPatch>>>
  filenamePattern: Ref<string>
  previewFilename: (uuid: string) => string | null

  touchedCount: ComputedRef<number>
  mixedFields: ComputedRef<Set<ScalarField | "watermark">>

  applyToAll: () => Promise<BatchResult>
  failed: Ref<FailedItem[]>
}

const SCALAR_FIELDS: ScalarField[] = [
  "copyright",
  "copyrightUrl",
  "license",
  "licenseUrl",
  "sourceUrl"
]

const ARRAY_FIELDS: ArrayField[] = [
  "tags",
  "companies",
  "gameSeries",
  "games",
  "gameConsoles"
]

export function useBatchEditSession(
  options: BatchEditSessionOptions
): BatchEditSession {
  // Snapshot external items at creation. removeItem + applyToAll mutate local
  // state; external changes to options.items are intentionally not synced
  // (callers that need this use a :key to force remount).
  const items = ref<BatchEditItem[]>([...options.items.value]) as Ref<BatchEditItem[]>

  const scalarPatch = ref<Partial<Record<ScalarField, string>>>({})
  const watermarkMode = ref<WatermarkMode>("noop")
  const arrayPatches = ref<Partial<Record<ArrayField, ArrayPatch>>>({})
  const filenamePattern = ref("")

  // Series numbers are fixed at session start so a re-apply after partial
  // failure keeps each item's number (renumbering would produce duplicates).
  const seriesTotal = items.value.length
  const seriesIndex = new Map(items.value.map((item, i) => [item.uuid, i]))

  function previewFilename(uuid: string): string | null {
    const index = seriesIndex.get(uuid)
    const item = items.value.find(i => i.uuid === uuid)
    if (!filenamePattern.value.trim() || index === undefined || !item) return null
    return seriesFilename(filenamePattern.value, index, seriesTotal, item.filename)
  }

  const failed = ref<FailedItem[]>([])

  const mixedFields = computed<Set<ScalarField | "watermark">>(() => {
    const result = new Set<ScalarField | "watermark">()
    const list = items.value
    if (list.length < 2) return result

    const first = list[0]!
    for (const field of SCALAR_FIELDS) {
      if (list.some(i => i[field] !== first[field])) {
        result.add(field)
      }
    }
    if (list.some(i => i.watermark !== first.watermark)) {
      result.add("watermark")
    }
    return result
  })

  const touchedCount = computed(() => {
    let n = Object.keys(scalarPatch.value).length
    if (watermarkMode.value !== "noop") n++
    if (filenamePattern.value.trim()) n++
    n += Object.values(arrayPatches.value).filter(p => p && p.mode !== "noop").length
    return n
  })

  function removeItem(uuid: string): void {
    items.value = items.value.filter(i => i.uuid !== uuid)
  }

  function buildUpdateForItem(item: BatchEditItem): MediaUpdateInput {
    const update: MediaUpdateInput = {}

    const filename = previewFilename(item.uuid)
    if (filename !== null) {
      update.filename = filename
    }

    for (const field of SCALAR_FIELDS) {
      const v = scalarPatch.value[field]
      if (v !== undefined) {
        ;(update as Record<string, unknown>)[field] = v
      }
    }

    if (watermarkMode.value !== "noop") {
      update.watermark = watermarkMode.value === "on"
    }

    for (const field of ARRAY_FIELDS) {
      const patch = arrayPatches.value[field]
      if (!patch || patch.mode === "noop") continue
      const current = item[field] as EntityRef[]
      const merged = computePerItemArray(patch.mode, current, patch.values)
      ;(update as Record<string, unknown>)[field] = merged.map(e => e.uuid)
    }

    return update
  }

  async function applyToAll(): Promise<BatchResult> {
    const current = items.value
    const results = await Promise.allSettled(
      current.map(async (item) => {
        const update = buildUpdateForItem(item)
        await options.save(item.uuid, update)
        return item.uuid
      })
    )

    const succeeded: string[] = []
    const failedList: FailedItem[] = []

    for (let i = 0; i < results.length; i++) {
      const r = results[i]!
      const item = current[i]!
      if (r.status === "fulfilled") {
        succeeded.push(item.uuid)
      } else {
        const reason = r.reason
        failedList.push({
          uuid: item.uuid,
          filename: item.filename,
          error: reason instanceof Error ? reason : new Error(String(reason))
        })
      }
    }

    const failedIds = new Set(failedList.map(f => f.uuid))
    items.value = current.filter(i => failedIds.has(i.uuid))
    failed.value = failedList

    return { succeeded, failed: failedList }
  }

  return {
    items,
    removeItem,
    scalarPatch,
    watermarkMode,
    arrayPatches,
    filenamePattern,
    previewFilename,
    touchedCount,
    mixedFields,
    applyToAll,
    failed
  }
}
